import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { LanguageSelector } from '../components/LanguageSelector';
import { TierBadge } from '../components/TierBadge';
import { Footer } from '../components/Footer';
import { useI18n } from '../contexts/I18nContext';
import { useStarredProjects } from '../hooks/useStarredProjects';
import { CreditBalanceBadge } from '../components/CreditBalanceBadge';
import { MiniAppType, SubscriptionTier } from '../lib/supabase';
import {
  Folders,
  ExternalLink,
  LogIn,
  LogOut,
  AlertCircle,
  RefreshCw,
  User,
  Star,
  LayoutDashboard,
  Menu,
  X,
} from 'lucide-react';

interface PublicProject {
  id: string;
  name: string;
  description: string | null;
  type?: MiniAppType | null;
  slug: string | null;
  public_id: string | null;
  required_tier: SubscriptionTier | null;
  is_promoted: boolean;
  owner_is_admin: boolean;
}

export function UtilitiesPage() {
  const { user, signOut } = useAuth();
  const { tt } = useI18n();
  const { isStarred, toggleStar } = useStarredProjects();

  const [projects, setProjects] = useState<PublicProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const fetchUtilityProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/rest/v1/rpc/get_public_projects`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const filteredProjects = (data || []).filter((project: PublicProject) => {
        const isPubliclyVisible = project.owner_is_admin || project.is_promoted === true;
        return isPubliclyVisible && project.type === 'utility';
      });

      setProjects(filteredProjects);
    } catch (err) {
      setError(err instanceof Error ? err.message : tt('Failed to load mini-apps'));
    } finally {
      setLoading(false);
    }
  }, [tt]);

  useEffect(() => {
    fetchUtilityProjects();
  }, [fetchUtilityProjects]);

  useEffect(() => {
    document.title = 'Utilities - Llambada';
  }, []);

  const getProjectUrl = (project: PublicProject) => {
    return `/p/${project.public_id}${project.slug ? `/${project.slug}` : ''}`;
  };

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh flex flex-col">
      <header className="sticky top-0 z-50 border-b border-theme-border-muted bg-theme-bg/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Llambada" className="h-5 w-5 rounded-md shadow-md shadow-accent-500/20" />
              <span className="font-display text-lg sm:text-xl font-semibold text-theme-text">Llambada</span>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-2">
              {!user && <LanguageSelector />}
              {user ? (
                <>
                  <CreditBalanceBadge />
                  <Link to="/dashboard" className="btn btn-secondary text-sm">
                    <LayoutDashboard className="h-4 w-4" />
                    {tt('Dashboard')}
                  </Link>
                  <ThemeToggle />
                  <Link to="/profile" className="btn btn-ghost text-sm" title={tt('Profile & Settings')}>
                    <User className="h-4 w-4" />
                  </Link>
                  <button onClick={() => signOut()} className="btn btn-ghost text-sm" title={tt('Sign out')}>
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <ThemeToggle />
                  <Link to="/login" className="btn btn-primary">
                    <LogIn className="h-4 w-4" />
                    {tt('Sign In')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile nav */}
            <div className="flex md:hidden items-center gap-2">
              {!user && <LanguageSelector />}
              {user && <CreditBalanceBadge />}
              <ThemeToggle />
              {user ? (
                <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="btn btn-ghost p-2" aria-label={tt('Toggle menu')}>
                  {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              ) : (
                <Link to="/login" className="btn btn-primary text-sm px-3 py-2">
                  <LogIn className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {showMobileMenu && user && (
            <div className="md:hidden border-t border-theme-border-muted py-3 space-y-1 animate-fade-in">
              <Link to="/dashboard" className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors" onClick={() => setShowMobileMenu(false)}>
                <LayoutDashboard className="h-4 w-4" />
                {tt('Dashboard')}
              </Link>
              <Link to="/profile" className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors" onClick={() => setShowMobileMenu(false)}>
                <User className="h-4 w-4" />
                {tt('Profile & Settings')}
              </Link>
              <button onClick={() => { signOut(); setShowMobileMenu(false); }} className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left">
                <LogOut className="h-4 w-4" />
                {tt('Sign out')}
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
          <div className="text-center mb-6 sm:mb-10">
            <nav className="mb-4 text-sm text-theme-text-muted">
              <Link to="/" className="hover:text-accent-500 transition-colors">All apps</Link>
              <span className="px-2">&gt;</span>
              <span className="text-theme-text">Utilities</span>
            </nav>
            <h1 className="font-display text-2xl sm:text-4xl md:text-5xl font-bold text-theme-text mb-3 sm:mb-4">
              Utilities
            </h1>
            <p className="text-base sm:text-xl text-theme-text-secondary max-w-2xl mx-auto">
              Browse utility mini-apps only
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-error-bg mb-6">
                <AlertCircle className="h-8 w-8 text-theme-error" />
              </div>
              <h3 className="font-display text-xl font-semibold text-theme-text mb-2">
                {tt('Failed to load mini-apps')}
              </h3>
              <p className="text-theme-text-secondary mb-6">
                {error}
              </p>
              <button
                onClick={() => fetchUtilityProjects()}
                className="btn btn-primary"
              >
                <RefreshCw className="h-4 w-4" />
                {tt('Try Again')}
              </button>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-surface mb-6">
                <Folders className="h-8 w-8 text-theme-text-muted" />
              </div>
              <h3 className="font-display text-xl font-semibold text-theme-text mb-2">
                {tt('No public utilities yet')}
              </h3>
              <p className="text-theme-text-secondary mb-6">
                {tt('Check back later for new utility mini-apps.')}
              </p>
              <button
                onClick={() => fetchUtilityProjects()}
                className="btn btn-secondary"
              >
                <RefreshCw className="h-4 w-4" />
                {tt('Refresh')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <Link
                  key={project.id}
                  to={getProjectUrl(project)}
                  className="card p-6 hover:border-accent-500/50 transition-all duration-200 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 group-hover:from-accent-500/30 group-hover:to-cyan-500/30 transition-colors">
                      <Folders className="h-6 w-6 text-accent-500" />
                    </div>
                    <div className="flex items-center gap-2">
                      {project.required_tier && (
                        <TierBadge tier={project.required_tier} size="sm" />
                      )}
                      {user && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleStar(project.id);
                          }}
                          className="p-0.5 rounded transition-colors hover:bg-theme-surface-hover"
                          title={isStarred(project.id) ? tt('Unstar') : tt('Star')}
                        >
                          <Star
                            className={`h-4 w-4 transition-colors ${
                              isStarred(project.id)
                                ? 'text-amber-500'
                                : 'text-theme-text-muted opacity-50 group-hover:text-theme-text-muted group-hover:opacity-70'
                            }`}
                            fill={isStarred(project.id) ? 'currentColor' : 'none'}
                          />
                        </button>
                      )}
                      <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-accent-500 transition-colors" />
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-theme-text mb-2 group-hover:text-accent-500 transition-colors">
                    {project.name}
                  </h3>
                  {project.description && (
                    <p className="text-theme-text-secondary text-sm line-clamp-2">
                      {project.description}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer variant="full" />
    </div>
  );
}
